import React, { forwardRef } from 'react';

const STREAK_TIERS = [
  { min: 0, max: 0, label: '--', labelEn: '--', color: 'transparent', bg: 'transparent' },
  { min: 1, max: 2, label: 'BRAISE', labelEn: 'EMBER', color: '#F97316', bg: '#1C1310' },
  { min: 3, max: 4, label: 'FLAMME', labelEn: 'FLAME', color: '#EF4444', bg: '#1A0F0F' },
  { min: 5, max: 7, label: 'BRASIER', labelEn: 'BLAZE', color: '#DC2626', bg: '#1A0808' },
  { min: 8, max: Infinity, label: 'INFERNO', labelEn: 'INFERNO', color: '#FF0040', bg: '#1A0510' },
];

const GRADE_COLORS = {
  recrue: { color: '#71717A', label: 'DEBUTANT' },
  gardien: { color: '#3B82F6', label: 'GARDIEN' },
  elite: { color: '#A855F7', label: 'ELITE' },
  legende: { color: '#FFD700', label: 'LEGENDE EXTRA MILES' },
};

function getTier(streak) {
  return STREAK_TIERS.find(t => streak >= t.min && streak <= t.max) || STREAK_TIERS[0];
}

const StreakShareCard = forwardRef(function StreakShareCard({ streak = 0, gradeId = 'recrue', userName = '' }, ref) {
  const tier = getTier(streak);
  const grade = GRADE_COLORS[gradeId] || GRADE_COLORS.recrue;

  return (
    <div
      ref={ref}
      data-testid="streak-share-card"
      style={{
        width: 420,
        height: 560,
        background: `linear-gradient(180deg, ${tier.bg} 0%, #0A0A0A 40%, #0D0D0D 100%)`,
        borderRadius: 24,
        padding: 32,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
        position: 'relative',
        overflow: 'hidden',
        border: `2px solid ${tier.color}30`,
      }}
    >
      {/* Radial glow */}
      <div style={{
        position: 'absolute',
        top: -80,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 300,
        height: 300,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${tier.color}15 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      {/* Top branding */}
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <div style={{
          fontSize: 11,
          letterSpacing: 6,
          color: '#525252',
          textTransform: 'uppercase',
          marginBottom: 4,
        }}>
          EXTRA MILES FITNESS
        </div>
        <div style={{
          fontSize: 28,
          fontWeight: 900,
          letterSpacing: 4,
          color: '#FAFAFA',
          textTransform: 'uppercase',
        }}>
          YUMIX
        </div>
      </div>

      {/* Flame + Streak count */}
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        {/* Flame SVG */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
          <svg viewBox="0 0 24 24" width={80} height={80} style={{
            filter: `drop-shadow(0 0 ${Math.min(streak, 15)}px ${tier.color}90)`,
          }}>
            <defs>
              <linearGradient id="shareFlameGrad" x1="0%" y1="100%" x2="0%" y2="0%">
                <stop offset="0%" stopColor="#FF6B00" />
                <stop offset="40%" stopColor={tier.color} />
                <stop offset="100%" stopColor="#FFD700" />
              </linearGradient>
            </defs>
            <path
              d="M12 2C12 2 7 7 7 12C7 14.8 8.6 17.2 11 18.3V20C11 20.6 11.4 21 12 21C12.6 21 13 20.6 13 20V18.3C15.4 17.2 17 14.8 17 12C17 7 12 2 12 2Z"
              fill="url(#shareFlameGrad)"
            />
            {streak >= 5 && (
              <path
                d="M12 6C12 6 9.5 9 9.5 12C9.5 13.9 10.6 15.5 12 16C13.4 15.5 14.5 13.9 14.5 12C14.5 9 12 6 12 6Z"
                fill="#FFD700"
                opacity="0.7"
              />
            )}
          </svg>
        </div>

        {/* Streak number */}
        <div style={{
          fontSize: 72,
          fontWeight: 900,
          color: tier.color,
          lineHeight: 1,
          textShadow: `0 0 30px ${tier.color}40`,
        }}>
          {streak}
        </div>
        <div style={{
          fontSize: 14,
          letterSpacing: 5,
          color: '#A3A3A3',
          textTransform: 'uppercase',
          marginTop: 4,
        }}>
          JOURS CONSECUTIFS
        </div>

        {/* Tier label */}
        <div style={{
          marginTop: 16,
          display: 'inline-block',
          padding: '6px 20px',
          borderRadius: 100,
          border: `1.5px solid ${tier.color}50`,
          background: `${tier.color}10`,
        }}>
          <span style={{
            fontSize: 13,
            fontWeight: 800,
            letterSpacing: 4,
            color: tier.color,
            textTransform: 'uppercase',
          }}>
            {tier.label}
          </span>
        </div>
      </div>

      {/* Grade + User */}
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        {/* Grade badge */}
        <div style={{
          display: 'inline-block',
          padding: '5px 16px',
          borderRadius: 6,
          border: `1px solid ${grade.color}40`,
          background: `${grade.color}12`,
          marginBottom: 8,
        }}>
          <span style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 3,
            color: grade.color,
            textTransform: 'uppercase',
          }}>
            {grade.label}
          </span>
        </div>

        {userName && (
          <div style={{
            fontSize: 14,
            color: '#737373',
            fontWeight: 500,
          }}>
            {userName}
          </div>
        )}

        {/* Bottom divider + tagline */}
        <div style={{
          marginTop: 12,
          paddingTop: 12,
          borderTop: '1px solid #262626',
        }}>
          <div style={{
            fontSize: 10,
            letterSpacing: 4,
            color: '#404040',
            textTransform: 'uppercase',
          }}>
            ALL IN ALL TIME
          </div>
        </div>
      </div>
    </div>
  );
});

export default StreakShareCard;
